import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import Sidebar from './Sidebar';
import {
    createUserPost,
    updateUserPost,
    submitUserPost,
    fetchUserCategories,
    fetchMyPosts,
    fetchUserProfile
} from '../../services/api';

const DEFAULT_FEATURED_IMAGE =
    'https://res.cloudinary.com/dnbbm9vzi/image/upload/v1736181865/web_development___website_webpage_browser_ad_advertisement_man_people_ibpgaq.png';

const getCloudinaryUrl = (img) => {
  if (!img) return DEFAULT_FEATURED_IMAGE;
  if (img.includes('image/upload/')) {
    return `https://res.cloudinary.com/dbm8xbouw/${img}`;
  }
  return `https://res.cloudinary.com/dbm8xbouw/image/upload/${img}`;
};

const StoryEditor = () => {
    const token = localStorage.getItem('authToken');
    const { slug } = useParams();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [title, setTitle] = useState('');
    const [excerpt, setExcerpt] = useState('');
    const [content, setContent] = useState('');
    const [category, setCategory] = useState(null);
    const [language, setLanguage] = useState('en');
    const [categories, setCategories] = useState([]);
    const [postSlug, setPostSlug] = useState(slug || null);
    const [featuredImage, setFeaturedImage] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [saved, setSaved] = useState(false);

    const [sidebarCollapsed, setSidebarCollapsed] = useState(
        () => JSON.parse(localStorage.getItem('sidebarCollapsed')) || false
    );

    useEffect(() => {
        localStorage.setItem('sidebarCollapsed', JSON.stringify(sidebarCollapsed));
    }, [sidebarCollapsed]);

    const autosaveTimer = useRef(null);
    const isReady = title.trim() && content.trim() && category;

    // <-- Add the user profile state & fetch here
    const [userProfile, setUserProfile] = useState(null);

    // Load user profile
    useEffect(() => {
        const loadProfile = async () => {
            if (!token) return;
            try {
                const profileData = await fetchUserProfile(token);
                setUserProfile(profileData);
            } catch (err) {
                console.error('Error fetching user profile:', err);
            }
        };
        loadProfile();
    }, [token]);


    // Load categories
    useEffect(() => {
        const loadCategories = async () => {
            try {
                const data = await fetchUserCategories(token);
                setCategories(data);
                if (data.length > 0 && !category) setCategory(data[0].id);
            } catch (err) {
                console.error('Error loading categories:', err);
            }
        };
        loadCategories();
    }, [token]);

    // Load existing post
    useEffect(() => {
        const loadPost = async () => {
            if (!slug) {
                setLoading(false);
                return;
            }
            try {
                const myPosts = await fetchMyPosts(token);
                const post = myPosts.find(p => p.slug === slug);
                if (!post) throw new Error('Post not found');

                setPostSlug(post.slug);
                setTitle(post.title || '');
                setExcerpt(post.excerpt || '');
                setContent(post.content || '');
                setCategory(post.category?.id || null);
                setLanguage(post.language || 'en');
                setFeaturedImage(post.featured_image || null);
            } catch (err) {
                console.error(err);
                alert('Failed to load the post.');
                navigate('/stories');
            } finally {
                setLoading(false);
            }
        };
        loadPost();
    }, [slug, token, navigate]);

    // Autosave (updates only)
    useEffect(() => {
        if (!isReady || !postSlug) return;

        if (autosaveTimer.current) clearTimeout(autosaveTimer.current);

        autosaveTimer.current = setTimeout(async () => {
            setIsSaving(true);
            try {
                const formData = new FormData();
                formData.append('title', title);
                formData.append('excerpt', excerpt);
                formData.append('content', content);
                formData.append('category', category);
                formData.append('language', language);
                if (featuredImage instanceof File) {
                    formData.append('featured_image', featuredImage);
                }

                await updateUserPost(postSlug, formData, token);
                setSaved(true);
                setTimeout(() => setSaved(false), 1500);
            } catch (err) {
                console.error('Autosave failed:', err);
            } finally {
                setIsSaving(false);
            }
        }, 2000);

        return () => clearTimeout(autosaveTimer.current);
    }, [title, excerpt, content, category, language, featuredImage, postSlug]);

    const handleSaveDraft = async () => {
    if (!isReady) return;

    setIsSaving(true);
    try {
        const formData = new FormData();
        formData.append('title', title);
        formData.append('excerpt', excerpt);
        formData.append('content', content);
        formData.append('category', category);
        formData.append('language', language);
        if (featuredImage instanceof File) {
            formData.append('featured_image', featuredImage);
        }

        let slugResult = postSlug;
        if (postSlug) {
            await updateUserPost(postSlug, formData, token);
        } else {
            const created = await createUserPost(formData, token);
            slugResult = created.slug;
            setPostSlug(created.slug);
        }

        setSaved(true);
        setTimeout(() => setSaved(false), 1500);
    } catch (err) {
        console.error('Error saving draft:', err);
        alert('Error saving draft');
    } finally {
        setIsSaving(false);
    }
};


    const handleSubmit = async () => {
        if (!isReady) return;
        setIsSaving(true);
        try {
            if (!postSlug) {
                const formData = new FormData();
                formData.append('title', title);
                formData.append('excerpt', excerpt);
                formData.append('content', content);
                formData.append('category', category);
                formData.append('language', language);
                if (featuredImage instanceof File) {
                    formData.append('featured_image', featuredImage);
                }

                const created = await createUserPost(formData, token);
                setPostSlug(created.slug);
            }

            await submitUserPost(postSlug, token);
            navigate('/stories');
        } catch (err) {
            console.error('Error submitting post:', err);
            alert('Error submitting post');
        } finally {
            setIsSaving(false);
        }
    };

    if (loading) return <p className="text-center mt-5">Loading editor...</p>;

    return (
        <div className="dashboard-background">
            <div className="container-fluid">
                <div className="d-flex">
                    <Sidebar userProfile={userProfile}
                        isCollapsed={sidebarCollapsed}
                        setIsCollapsed={setSidebarCollapsed}
                    />
                    <main className="flex-grow-1 px-4"
                        style={{
                            minHeight: '80vh',
                            marginLeft: sidebarCollapsed ? '60px' : '220px'
                        }}>
                        {/* Top bar */}
                        <div className="d-flex justify-content-between align-items-center py-3 border-bottom">
                            <button className="btn btn-outline-dark fw-bold" onClick={() => navigate('/stories')}>
                                ← Back
                            </button>

                            <div className="d-flex align-items-center gap-2">
                                {isSaving && <span className="text-muted">Saving...</span>}
                                {saved && <span className="text-success">Saved ✓</span>}

                                {/* Styled Category Selector */}
                                <div className="dropdown">
                                    <button
                                        className="btn btn-outline-dark dropdown-toggle fw-bold"
                                        type="button"
                                        data-bs-toggle="dropdown"
                                        aria-expanded="false"
                                        style={{ minWidth: '160px' }}
                                    >
                                        {categories.find(cat => cat.id === category)?.name || 'Select Category'}
                                    </button>
                                    <ul className="dropdown-menu">
                                        {categories.map(cat => (
                                            <li key={cat.id}>
                                                <button
                                                    className="dropdown-item"
                                                    type="button"
                                                    onClick={() => setCategory(cat.id)}
                                                >
                                                    {cat.name}
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                </div>

                                <button
                                    onClick={handleSaveDraft}
                                    className="btn btn-dark fw-bold"
                                    disabled={!isReady || isSaving}
                                >
                                    Save Draft
                                </button>
                                <button
                                    onClick={handleSubmit}
                                    className="btn btn-primary fw-bold"
                                    disabled={!isReady || isSaving}
                                >
                                    Submit
                                </button>
                            </div>
                        </div>

                        {/* Editor area */}
                        <div className="mx-auto mt-4" style={{ maxWidth: '800px' }}>
                            
                            <input
                                type="text"
                                placeholder="Title"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                className="w-100 border-0 border-bottom p-2 mb-3 fs-2 fw-bold"
                                style={{
                                    outline: 'none',
                                    backgroundColor: '#f8f9fa', // match your page background
                                    color: '#000',              // text color
                                }}
                            />
                            <input
                                type="text"
                                placeholder="Subtitle / Excerpt"
                                value={excerpt}
                                onChange={(e) => setExcerpt(e.target.value)}
                                className="w-100 border-0 border-bottom p-2 mb-3 text-muted"
                                style={{
                                    outline: 'none',
                                    backgroundColor: '#f8f9fa', // match your page background
                                    color: '#000',              // text color
                                }}
                            />

                            {/* Featured image upload */}
                            <div className="mb-3">
                                <label className="form-label fw-bold">Article Image (optional)</label>
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={(e) => {
                                        const file = e.target.files[0];
                                        if (file) setFeaturedImage(file);
                                    }}
                                    className="form-control mb-2"
                                    style={{
                                        outline: 'none',
                                        backgroundColor: '#f8f9fa', // match your page background
                                        color: '#000',              // text color
                                    }}
                                />
                                {featuredImage && (
                                    <img
                                        src={featuredImage instanceof File ? URL.createObjectURL(featuredImage) : getCloudinaryUrl(featuredImage)}
                                        alt="Featured"
                                        style={{ maxWidth: '300px', maxHeight: '200px' }}
                                    />
                                )}

                            </div>

                            <ReactQuill
                                theme="snow"
                                value={content}
                                onChange={setContent}
                                modules={{
                                    toolbar: [
                                        [{ header: [1, 2, 3, false] }],
                                        ['bold', 'italic', 'underline', 'strike'],
                                        [{ list: 'ordered' }, { list: 'bullet' }],
                                        ['link', 'image'],
                                        ['clean']
                                    ]
                                }}
                                placeholder="Write your story here..."
                                style={{
                                    minHeight: '600px',       // larger editable area
                                    maxHeight: '80vh',        // optional, prevent editor from growing too tall
                                    marginBottom: '1rem',
                                }}
                            />
                        </div>
                    </main>
                </div>
            </div>
        </div>
    );
};

export default StoryEditor;
